import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { ApiError } from "@hostdock/sdk";
import type { Permission, PermissionInfo, RoleSummary } from "@hostdock/types";
import { ActionMenu, Badge, Button, Checkbox, ConfirmDialog, DataTable, Dialog, Field, FormError, Input, PageHeader, Panel, toast } from "@hostdock/ui";
import { api } from "../api";
import { RequirePermission } from "../lib/guard";
import { useSession } from "../session";

export function RolesPage() {
  return (
    <RequirePermission anyOf={["SECURITY_MANAGE"]}>
      <Roles />
    </RequirePermission>
  );
}

export function usePermissionCatalogue() {
  return useQuery({ queryKey: ["permissions"], queryFn: api.permissions, staleTime: Infinity });
}

function Roles() {
  const qc = useQueryClient();
  const { session } = useSession();
  const roles = useQuery({ queryKey: ["roles"], queryFn: api.roles.list });
  const [editing, setEditing] = useState<RoleSummary | "new" | null>(null);
  const [deleting, setDeleting] = useState<RoleSummary | null>(null);
  const remove = useMutation({ meta: { inlineError: true },
    mutationFn: (r: RoleSummary) => api.roles.remove(r.id),
    onSuccess: () => {
      toast.success("Role deleted");
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ["roles"] });
    },
  });

  return (
    <>
      <PageHeader title="Roles" description="Bundles of permissions assigned to admins." actions={<Button variant="primary" icon={<Plus size={15} />} onClick={() => setEditing("new")}>New role</Button>} />
      <Panel flush>
        <DataTable
          rows={roles.data?.items}
          loading={roles.isPending}
          error={roles.error}
          onRetry={() => void roles.refetch()}
          rowKey={(r) => r.id}
          columns={[
            {
              key: "name",
              header: "Role",
              cell: (r) => (
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-medium">
                    {r.name}
                    {r.isSystem && <Badge><Lock size={10} /> Built-in</Badge>}
                    {r.id === session.role.id && <Badge tone="accent">Your role</Badge>}
                  </p>
                  {r.description && <p className="truncate text-[12px] text-muted">{r.description}</p>}
                </div>
              ),
            },
            { key: "perms", header: "Permissions", cell: (r) => <span className="tabular-nums">{r.permissions.length}</span> },
            { key: "admins", header: "Admins", cell: (r) => <span className="tabular-nums">{r.adminCount}</span> },
            {
              key: "actions",
              header: "",
              className: "w-10 text-right",
              cell: (r) => (
                <ActionMenu
                  actions={[
                    { label: r.isSystem ? "View permissions" : "Edit", onSelect: () => setEditing(r) },
                    { label: "Delete", onSelect: () => setDeleting(r), danger: true, hidden: r.isSystem || r.adminCount > 0 },
                  ]}
                />
              ),
            },
          ]}
        />
      </Panel>
      {editing && <RoleEditor role={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete role ${deleting?.name}?`}
        confirmLabel="Delete role"
        confirmText={deleting?.name}
        loading={remove.isPending}
        error={remove.error}
        onConfirm={() => deleting && remove.mutate(deleting)}
      />
    </>
  );
}

export function groupPermissions(items: PermissionInfo[]) {
  const groups = new Map<string, PermissionInfo[]>();
  for (const p of items) groups.set(p.group, [...(groups.get(p.group) ?? []), p]);
  return [...groups.entries()];
}

function RoleEditor({ role, onClose }: { role: RoleSummary | null; onClose(): void }) {
  const qc = useQueryClient();
  const { session } = useSession();
  const catalogue = usePermissionCatalogue();
  const readOnly = role?.isSystem ?? false;
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [selected, setSelected] = useState<Set<Permission>>(new Set(role?.permissions ?? []));
  const held = useMemo(() => new Set(session.permissions), [session.permissions]);
  const isOwner = session.role.name === "Owner";
  const save = useMutation({ meta: { inlineError: true },
    mutationFn: () => {
      const body = { name, description, permissions: [...selected] };
      return role ? api.roles.update(role.id, body) : api.roles.create(body);
    },
    onSuccess: () => {
      toast.success(role ? "Role updated" : "Role created");
      void qc.invalidateQueries({ queryKey: ["roles"] });
      onClose();
    },
  });
  const fe = save.error instanceof ApiError ? save.error.fieldErrors : {};
  const toggle = (p: Permission, on: boolean) => setSelected((s) => {
    const n = new Set(s);
    if (on) n.add(p);
    else n.delete(p);
    return n;
  });

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      size="lg"
      title={role ? (readOnly ? role.name : `Edit ${role.name}`) : "New role"}
      description={readOnly ? "Built-in roles cannot be edited. The Owner role always holds every permission." : "Permissions take effect on each admin's next request."}
      footer={
        readOnly ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" loading={save.isPending} disabled={name.trim().length < 2} onClick={() => save.mutate()}>
              {role ? "Save role" : "Create role"}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-5">
        {!readOnly && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" error={fe.name}>{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} autoFocus />}</Field>
            <Field label="Description" error={fe.description}>{(p) => <Input {...p} value={description} onChange={(e) => setDescription(e.target.value)} />}</Field>
          </div>
        )}
        {catalogue.error ? <FormError error={catalogue.error} /> : null}
        <div className="grid gap-5 sm:grid-cols-2">
          {groupPermissions(catalogue.data?.items ?? []).map(([group, perms]) => (
            <fieldset key={group} className="flex flex-col gap-2.5">
              <legend className="mb-1 text-[11.5px] font-semibold tracking-wider text-subtle uppercase">{group}</legend>
              {perms.map((p) => (
                <Checkbox
                  key={p.key}
                  checked={selected.has(p.key)}
                  onChange={(v) => toggle(p.key, v)}
                  disabled={readOnly || (!isOwner && !held.has(p.key))}
                  label={<span className="font-mono text-[12px]">{p.key}</span>}
                  description={p.description}
                />
              ))}
            </fieldset>
          ))}
        </div>
        {!readOnly && !isOwner && <p className="text-[12.5px] text-subtle">Permissions you don't hold yourself can't be granted.</p>}
        {save.error && !Object.keys(fe).length ? <FormError error={save.error} /> : null}
      </div>
    </Dialog>
  );
}
